import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Formik, Field, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
} from "reactstrap";
import { AppDispatch, RootState } from "slices/store";
import { setAdmin } from "slices/admin/adminSlice";
import profile_placeholder from "assets/images/profile_placeholder.png";
import {
  profileImageUpdate,
  updateAdminDetails,
  passwordUpdate,
} from "../../../endpoints/settings/index.action";

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { admin } = useSelector((state: RootState) => state.Admin);
  const dispatch = useDispatch<AppDispatch>();

  // Validation Schemas
  const adminDetailsSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, t("settings.firstNameMin"))
      .max(50, t("settings.firstNameMax"))
      .matches(/^[a-zA-Z]*$/, t("settings.firstNameLettersOnly")),
    lastName: Yup.string()
      .min(2, t("settings.lastNameMin"))
      .max(50, t("settings.lastNameMax"))
      .matches(/^[a-zA-Z]*$/, t("settings.lastNameLettersOnly")),
    email: Yup.string().email(t("settings.invalidEmail")),
  });

  const passwordUpdateSchema = Yup.object().shape({
    current_password: Yup.string()
      .required(t("settings.currentPasswordRequired")),
    new_password: Yup.string()
      .required(t("settings.newPasswordRequired")),
    confirm_new_password: Yup.string()
      .oneOf([Yup.ref("new_password")], t("settings.passwordsMustMatch"))
      .required(t("settings.confirmPasswordRequired")),
  });

  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<string | undefined>(admin?.profileImage);

  // Password visibility states for each field
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Admin Details Mutation
  const adminDetailsMutation = useMutation({
    mutationFn: updateAdminDetails,
    onSuccess: (data) => {
      if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
        // Map backend response to frontend format
        const updatedData = {
          id: data?.data?.id || admin?.id,
          firstName: data?.data?.firstName || admin?.firstName,
          lastName: data?.data?.lastName || admin?.lastName,
          email: data?.data?.email || admin?.email,
          role: data?.data?.role || admin?.role,
          profileImage: data?.data?.profileImage || admin?.profileImage,
        };
        dispatch(setAdmin(updatedData));
        toast.success(data?.message || t("settings.profileDetailsUpdated"), {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(data?.message || data?.response || t("settings.failedToUpdateProfile"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    },
    onError: (error: any) => {
      console.error("Admin details update error:", error);
      toast.error(error?.message || t("settings.failedToUpdateProfile"), {
        position: "top-right",
        autoClose: 3000,
      });
    },
  });

  // Password Update Mutation
  const passwordMutation = useMutation({
    mutationFn: passwordUpdate,
    onSuccess: (data) => {
      if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
        toast.success(data?.message || t("settings.passwordUpdated"), {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(data?.message || data?.response || t("settings.failedToUpdatePassword"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    },
    onError: (error: any) => {
      console.error("Password update error:", error);
      toast.error(error?.message || t("settings.failedToUpdatePassword"), {
        position: "top-right",
        autoClose: 3000,
      });
    },
  });

  // Profile Image Update Mutation
  const profileUpdateMutation = useMutation({
    mutationFn: profileImageUpdate,
    onSuccess: (data) => {
      if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
        const newProfileImage = data?.data?.profileImage || data?.data?.profile_image || data?.data?.image_url;
        // Update admin state with new profile image and any other updated fields
        const updatedAdmin = {
          ...admin,
          profileImage: newProfileImage,
          ...(data?.data ? {
            id: data.data.id || admin?.id,
            firstName: data.data.firstName || admin?.firstName,
            lastName: data.data.lastName || admin?.lastName,
            email: data.data.email || admin?.email,
            role: data.data.role || admin?.role,
          } : {}),
        };
        dispatch(setAdmin(updatedAdmin));
        setPhoto(newProfileImage);
        toast.success(data?.message || t("settings.profileImageUpdated"), {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(data?.message || data?.response || t("settings.failedToUpdateImage"), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    },
    onError: (error: any) => {
      console.error("Profile update error:", error);
      toast.error(error?.message || t("settings.failedToUpdateImage"), {
        position: "top-right",
        autoClose: 3000,
      });
    },
  });

  // Image handling functions
  const handleImageChange = (file: File) => {
    const data = new FormData();
    data.append("file", file);
    profileUpdateMutation.mutate(data);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleImageChange(file);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <h5 className="mb-0">{t("settings.general")}</h5>
        </CardHeader>
        <CardBody>
          {/* Profile Photo Section */}
          <div className="text-center mb-4">
            <div className="position-relative d-inline-block">
              <img
                src={photo || profile_placeholder}
                alt="Profile"
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "4px solid #fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              />
              <Button
                color="light"
                size="sm"
                className="position-absolute"
                style={{
                  bottom: "20px",
                  right: "0px",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => profilePhotoInputRef.current?.click()}
                disabled={profileUpdateMutation.isPending}
              >
                {profileUpdateMutation.isPending ? (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <i className="ri-pencil-fill fs-5"></i>
                )}
              </Button>
              <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {/* Admin Details Form */}
          <div>
            <h5 className="card-title mb-3">{t("settings.basicInfo")}</h5>
            <hr className="mt-0" style={{ opacity: 0.1 }} />
            <Formik
              initialValues={{
                firstName: admin?.firstName || "",
                lastName: admin?.lastName || "",
                email: admin?.email || "",
              }}
              validationSchema={adminDetailsSchema}
              onSubmit={(values, { setSubmitting, setValues }) => {
                // Filter out empty fields and email from payload (email is read-only)
                const filteredValues = Object.entries(values).reduce(
                  (acc, [key, value]) => {
                    // Exclude email from being sent to API since it's read-only
                    if (key === 'email') {
                      return acc;
                    }
                    if (value && value.toString().trim() !== "") {
                      acc[key] = value;
                    }
                    return acc;
                  },
                  {} as any
                );

                adminDetailsMutation.mutate(filteredValues, {
                  onSuccess: (data) => {
                    if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
                      // Update form fields with the new data from response
                      setValues({
                        firstName:
                          data?.data?.firstName || admin?.firstName || "",
                        lastName:
                          data?.data?.lastName || admin?.lastName || "",
                        email: data?.data?.email || admin?.email || "",
                      });
                    }
                  },
                  onSettled: () => setSubmitting(false),
                });
              }}
            >
              {({ errors, touched, isSubmitting, setValues, values }) => (
                <FormikForm>
                  <Row className="g-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="firstName">{t("settings.firstName")}</Label>
                        <Field
                          as={Input}
                          id="firstName"
                          name="firstName"
                          placeholder={t("settings.enterFirstName")}
                          className={`form-control-light ${
                            errors.firstName && touched.firstName
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {errors.firstName && touched.firstName && (
                          <FormFeedback>{errors.firstName}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="lastName">{t("settings.lastName")}</Label>
                        <Field
                          as={Input}
                          id="lastName"
                          name="lastName"
                          placeholder={t("settings.enterLastName")}
                          className={`form-control-light ${
                            errors.lastName && touched.lastName
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        {errors.lastName && touched.lastName && (
                          <FormFeedback>{errors.lastName}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="email">{t("settings.email")}</Label>
                        <Field
                          as={Input}
                          id="email"
                          name="email"
                          type="email"
                          placeholder={t("settings.enterEmail")}
                          disabled
                          className={`form-control-light ${
                            errors.email && touched.email ? "is-invalid" : ""
                          }`}
                          style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                        />
                        {errors.email && touched.email && (
                          <FormFeedback>{errors.email}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Save and Cancel Buttons */}
                  <div className="d-flex justify-content-end gap-3 mt-2">
                    <Button
                      color="danger"
                      type="button"
                      style={{ width: "110px" }}
                      onClick={() => {
                        // Reset to original admin values
                        setValues({
                          firstName: admin?.firstName || "",
                          lastName: admin?.lastName || "",
                          email: admin?.email || "",
                        });
                      }}
                      disabled={isSubmitting || adminDetailsMutation.isPending}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      color="success"
                      type="submit"
                      style={{ width: "110px" }}
                      disabled={
                        isSubmitting ||
                        adminDetailsMutation.isPending ||
                        (!values.firstName?.trim() &&
                          !values.lastName?.trim() &&
                          !values.email?.trim())
                      }
                    >
                      {isSubmitting || adminDetailsMutation.isPending ? (
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">{t("common.loading")}</span>
                        </div>
                      ) : (
                        t("common.save")
                      )}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>

          {/* Password Update Form */}
          <div className="mt-4">
            <h5 className="card-title mb-3">{t("settings.security")}</h5>
            <hr className="mt-0" style={{ opacity: 0.1 }} />
            <Formik
              initialValues={{
                current_password: "",
                new_password: "",
                confirm_new_password: "",
              }}
              validationSchema={passwordUpdateSchema}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                // Map form field names to API field names
                const passwordData = {
                  oldPassword: values.current_password,
                  newPassword: values.new_password,
                };
                
                passwordMutation.mutate(passwordData, {
                  onSuccess: (data) => {
                    // Only reset form if status is success
                    if (data?.status === "success" || data?.statusCode === 200 || data?.status_code === 200) {
                      resetForm();
                      // Also reset password visibility states on successful update
                      setShowCurrentPassword(false);
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }
                    // Toast messages are already handled in the mutation definition
                  },
                  onSettled: () => setSubmitting(false),
                });
              }}
            >
              {({ errors, touched, isSubmitting, values, resetForm }) => (
                <FormikForm>
                  <Row className="g-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="current_password">
                          {t("settings.currentPassword")}
                        </Label>
                        <div className="position-relative">
                          <Field
                            as={Input}
                            id="current_password"
                            name="current_password"
                            type={showCurrentPassword ? "text" : "password"}
                            placeholder={t("settings.enterCurrentPassword")}
                            className={`form-control-light ${
                              errors.current_password &&
                              touched.current_password
                                ? "is-invalid"
                                : ""
                            }`}
                            style={{ paddingRight: "45px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: "0",
                              width: "40px",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#6c757d",
                              zIndex: 5,
                            }}
                          >
                            <i
                              className={
                                showCurrentPassword
                                  ? "ri-eye-off-line"
                                  : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </div>
                        {errors.current_password &&
                          touched.current_password && (
                            <div className="invalid-feedback d-block">
                              {errors.current_password}
                            </div>
                          )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="new_password">{t("settings.newPassword")}</Label>
                        <div className="position-relative">
                          <Field
                            as={Input}
                            id="new_password"
                            name="new_password"
                            type={showNewPassword ? "text" : "password"}
                            placeholder={t("settings.enterNewPassword")}
                            className={`form-control-light ${
                              errors.new_password && touched.new_password
                                ? "is-invalid"
                                : ""
                            }`}
                            style={{ paddingRight: "45px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: "0",
                              width: "40px",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#6c757d",
                              zIndex: 5,
                            }}
                          >
                            <i
                              className={
                                showNewPassword
                                  ? "ri-eye-off-line"
                                  : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </div>
                        {errors.new_password && touched.new_password && (
                          <div className="invalid-feedback d-block">
                            {errors.new_password}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="confirm_password">
                          {t("settings.confirmNewPassword")}
                        </Label>
                        <div className="position-relative">
                          <Field
                            as={Input}
                            id="confirm_new_password"
                            name="confirm_new_password"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t("settings.enterConfirmNewPassword")}
                            className={`form-control-light ${
                              errors.confirm_new_password &&
                              touched.confirm_new_password
                                ? "is-invalid"
                                : ""
                            }`}
                            style={{ paddingRight: "45px" }}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: "0",
                              width: "40px",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#6c757d",
                              zIndex: 5,
                            }}
                          >
                            <i
                              className={
                                showConfirmPassword
                                  ? "ri-eye-off-line"
                                  : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </div>
                        {errors.confirm_new_password &&
                          touched.confirm_new_password && (
                            <div className="invalid-feedback d-block">
                              {errors.confirm_new_password}
                            </div>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Buttons */}
                  <div className="d-flex justify-content-end gap-3 mt-2">
                    <Button
                      color="danger"
                      type="button"
                      style={{ width: "110px" }}
                      onClick={() => {
                        resetForm();
                        // Also reset password visibility states
                        setShowCurrentPassword(false);
                        setShowNewPassword(false);
                        setShowConfirmPassword(false);
                      }}
                      disabled={isSubmitting || passwordMutation.isPending}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      color="success"
                      type="submit"
                      style={{ width: "110px" }}
                      disabled={
                        isSubmitting ||
                        !values.confirm_new_password ||
                        !values.new_password ||
                        !values.current_password ||
                        passwordMutation.isPending
                      }
                    >
                      {isSubmitting || passwordMutation.isPending ? (
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">{t("common.loading")}</span>
                        </div>
                      ) : (
                        t("settings.update")
                      )}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default SettingsPage;
